import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, RefreshCcw, Eye, Calendar, Users } from 'lucide-react';
type Tab = 'monthly' | 'private' | 'single';
interface Plan {
  name: string;
  freq: string;
  ageNote?: string;
  price: string;
  priceNote: string;
  body?: string;
  savings?: string;
  features: string[];
  hero?: boolean;
}
const MONTHLY_PLANS: Plan[] = [{
  name: 'Foundations',
  freq: '2x / week',
  ageNote: 'Youth · Ages 8–10',
  price: '$270',
  priceNote: '/ month',
  features: ['8 sessions per month', '$33.75 per session', '60-min sessions', 'Periodized programming', 'Testing every 4 weeks']
}, {
  name: 'Foundations',
  freq: '3x / week',
  ageNote: 'Youth · Ages 8–10',
  price: '$345',
  priceNote: '/ month',
  features: ['12 sessions per month', '$28.75 per session', '60-min sessions', 'Priority schedule access', 'Testing every 4 weeks']
}, {
  name: 'Performance Prep',
  freq: '2x / week',
  ageNote: 'MS · Ages 11–13',
  price: '$270',
  priceNote: '/ month',
  features: ['8 sessions per month', '$33.75 per session', '60-min sessions', 'Periodized programming', 'Testing every 4 weeks'],
  hero: true
}, {
  name: 'Performance Prep',
  freq: '3x / week',
  ageNote: 'MS · Ages 11–13',
  price: '$345',
  priceNote: '/ month',
  features: ['12 sessions per month', '$28.75 per session', '60-min sessions', 'Priority schedule access', 'Testing every 4 weeks']
}, {
  name: 'Advanced',
  freq: '2x / week',
  ageNote: 'HS · Ages 14+',
  price: '$325',
  priceNote: '/ month',
  features: ['8 sessions per month', '$40.63 per session', '75-min sessions', 'Periodized programming', 'Testing every 4 weeks']
}, {
  name: 'Advanced',
  freq: '3x / week',
  ageNote: 'HS · Ages 14+',
  price: '$415',
  priceNote: '/ month',
  features: ['12 sessions per month', '$34.58 per session', '75-min sessions', 'Priority schedule access', 'Testing every 4 weeks']
}];
const PRIVATE_1ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$600',
  priceNote: '/ month',
  features: ['4 sessions per month', '$150 per session', 'No discount']
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$1,080',
  priceNote: '/ month',
  savings: 'Save $120/mo',
  features: ['8 sessions per month', '$135 per session'],
  hero: true
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,530',
  priceNote: '/ month',
  savings: 'Save $270/mo',
  features: ['12 sessions per month', '$127.50 per session']
}];
const PRIVATE_2ON1: Plan[] = [{
  name: '1x / week',
  freq: 'Base Rate',
  price: '$500',
  priceNote: '/ month per athlete',
  features: ['4 sessions per month', '$125 per session', 'No discount']
}, {
  name: '2x / week',
  freq: 'Save 10%',
  price: '$900',
  priceNote: '/ month per athlete',
  features: ['8 sessions per month', '$112.50 per session'],
  hero: true
}, {
  name: '3x / week',
  freq: 'Save 15%',
  price: '$1,275',
  priceNote: '/ month per athlete',
  features: ['12 sessions per month', '$106.25 per session']
}];
const SINGLE_PLANS: Plan[] = [{
  name: 'Drop-In',
  freq: 'Single session',
  price: '$50',
  priceNote: '/ session',
  body: 'One class, any age group.',
  features: ['Try any age tier', 'No commitment', 'Reserve same week']
}, {
  name: '10-Session Card',
  freq: '10 sessions',
  price: '$450',
  priceNote: 'total',
  body: '$45 per session, fully flexible.',
  features: ['Use through Aug 8', 'Any age tier', 'Share within family'],
  hero: true
}, {
  name: 'Private 1-on-1',
  freq: 'Starting at',
  price: '$150',
  priceNote: '/ session',
  body: 'Final rate varies based on training frequency. The more sessions per week, the lower the per-session rate.',
  features: ['60 min with Coach Fernando', 'Custom programming', 'Position & sport-specific', 'Volume discounts available']
}];
const INCLUDED: string[] = ['Access to on-site recovery zone', 'Periodized programming', 'Performance testing every 4 weeks', 'Speed gates & jump mat testing', '10% sibling discount on additional athletes'];
const PRIVATE_INCLUDED: string[] = ["Individualized program built around your athlete's sport and goals.", 'Direct coaching from Fernando Beltran, Director of Performance.', 'Performance testing every 4 weeks, same markers, real data.', 'Full access to INPOWERiQ on-site recovery zone.', 'Speed and strength integrated into every session.', 'Make-ups allowed within the same week.'];
const POLICIES = [{
  Icon: RefreshCcw,
  title: 'Make-Ups',
  body: '1 free make-up per month, any age tier.'
}, {
  Icon: Calendar,
  title: 'Pay-In-Full',
  body: 'Full-summer enrollments are non-refundable.'
}, {
  Icon: Users,
  title: 'Holiday Closures',
  body: 'Closed July 3 & July 4. All other weeks run.'
}, {
  Icon: Eye,
  title: 'Parents Welcome',
  body: 'Observe any session from the parent gallery.'
}];
export function Pricing() {
  const [tab, setTab] = useState<Tab>('monthly');
  const plans = tab === 'monthly' ? MONTHLY_PLANS : tab === 'single' ? SINGLE_PLANS : [];
  return <div className="w-full" data-id="element-629">
      {/* HERO */}
      <section className="relative bg-reach-black overflow-hidden" data-id="element-630">
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-631">
          <div className="absolute top-1/4 -left-40 w-[50%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-50" data-id="element-632" />
          <div className="absolute top-[42%] -left-40 w-[35%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-30" data-id="element-633" />
          <div className="absolute top-[60%] -left-40 w-[25%] h-1.5 bg-reach-red transform -rotate-[6deg] opacity-20" data-id="element-634" />
        </div>
        <div className="max-w-container mx-auto px-4 md:px-8 py-20 md:py-24 text-center relative z-10" data-id="element-635">
          <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-4" data-id="element-636">
            Summer 2026 · Enrollment Open
          </p>
          <h1 className="font-bebas text-white text-6xl md:text-8xl tracking-wide leading-none mb-4" data-id="element-637">
            Pricing
          </h1>
          <p className="font-inter text-white/85 text-base md:text-xl max-w-2xl mx-auto" data-id="element-638">
            Group training, private coaching, and drop-in options.
          </p>
        </div>
      </section>

      {/* TAB TOGGLE */}
      <section className="bg-white sticky top-[64px] md:top-[72px] z-30 border-b border-black/5" data-id="element-639">
        <div className="max-w-container mx-auto px-4 md:px-8 py-5" data-id="element-640">
          <div className="flex justify-center" data-id="element-641">
            <div role="tablist" aria-label="Pricing options" className="inline-flex bg-reach-gray rounded-[4px] p-1 w-full sm:w-auto" data-id="element-642">
              <TabButton active={tab === 'monthly'} onClick={() => setTab('monthly')} data-id="element-643">
                Monthly
              </TabButton>
              <TabButton active={tab === 'private'} onClick={() => setTab('private')} data-id="element-644">
                Private Training
              </TabButton>
              <TabButton active={tab === 'single'} onClick={() => setTab('single')} data-id="element-645">
                Drop-In
              </TabButton>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING CARDS */}
      {tab === 'private' ? <PrivateTrainingTab data-id="element-646" /> : <section className="bg-white py-16 md:py-20" data-id="element-647">
          <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-648">
            {/* Helper line for monthly tab */}
            {tab === 'monthly' && <p className="font-inter text-center text-sm text-reach-black/60 uppercase tracking-[0.2em] mb-8" data-id="element-649">
                Prices reflect 2x or 3x training per week
              </p>}

            <div className={`grid gap-5 md:gap-6 ${tab === 'single' ? 'grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto'}`} data-id="element-650">
              {plans.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-651" />)}
            </div>

            <p className="font-inter text-center text-sm text-reach-black/60 mt-10 max-w-2xl mx-auto" data-id="element-652">
              All prices in USD. Auto-renewal on monthly plans can be cancelled
              with 14 days notice before the next billing cycle.
            </p>
          </div>
        </section>}

      {/* WHAT'S INCLUDED */}
      {tab !== 'private' && <section className="bg-reach-gray py-20 md:py-24" data-id="element-653">
          <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-654">
            <div className="text-center mb-10" data-id="element-655">
              <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-656">
                Every Plan Includes
              </p>
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-657">
                What's Included
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-658">
              {INCLUDED.map(item => <li key={item} className="flex items-start gap-3" data-id="element-659">
                  <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-660">
                    <Check size={14} className="text-white" strokeWidth={3} data-id="element-661" />
                  </div>
                  <span className="font-inter text-reach-black text-base" data-id="element-662">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </section>}

      {/* POLICIES */}
      {tab !== 'private' && <section className="bg-white py-16" data-id="element-663">
          <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-664">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-6 text-center" data-id="element-665">
              Policies
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-id="element-666">
              {POLICIES.map(({
            Icon,
            title,
            body
          }) => <div key={title} className="flex items-start gap-3" data-id="element-667">
                  <Icon size={18} className="text-reach-red shrink-0 mt-1" data-id="element-668" />
                  <div data-id="element-669">
                    <p className="font-bebas text-lg tracking-wider uppercase mb-1" data-id="element-670">
                      {title}
                    </p>
                    <p className="font-inter text-sm text-reach-black/70 leading-relaxed" data-id="element-671">
                      {body}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>}

      {/* FINAL CTA */}
      <section className="bg-reach-red py-16 md:py-20" data-id="element-672">
        <div className="max-w-container mx-auto px-4 md:px-8 text-center" data-id="element-673">
          <h2 className="font-bebas text-white text-5xl md:text-7xl tracking-wide mb-4" data-id="element-674">
            Ready to Start?
          </h2>
          <p className="font-inter text-white text-lg md:text-xl mb-10 max-w-2xl mx-auto" data-id="element-675">
            Reserve your athlete's spot for Summer 2026.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center max-w-md sm:max-w-none mx-auto" data-id="element-676">
            <a href="#top" onClick={e => {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          }} className="inline-flex items-center justify-center bg-white text-reach-black font-inter font-semibold px-6 min-h-[44px] md:min-h-[48px] rounded-[4px] transition-all duration-200 ease-out hover:bg-reach-black hover:text-white uppercase tracking-wide text-sm md:text-base hover:-translate-y-[3px]" data-id="element-677">
              Choose a Plan
            </a>
            <Link to="/book" className="btn-secondary-dark sm:w-auto" data-id="element-678">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>;
}
/* ---------- Private Training Tab ---------- */
function PrivateTrainingTab() {
  return <>
      <section className="bg-white py-16 md:py-20" data-id="element-679">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-680">
          {/* 1-on-1 Stack */}
          <div className="mb-16" data-id="element-681">
            <div className="text-center mb-8" data-id="element-682">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-683">
                1-on-1 Private Coaching
              </h2>
              <p className="font-inter text-reach-black/70 text-base" data-id="element-684">
                Base rate $150 per session
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-685">
              {PRIVATE_1ON1.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-686" />)}
            </div>
          </div>

          {/* 2-on-1 Stack */}
          <div data-id="element-687">
            <div className="text-center mb-8" data-id="element-688">
              <h2 className="font-bebas text-4xl md:text-5xl tracking-wide mb-2" data-id="element-689">
                2-on-1 Semi-Private
              </h2>
              <p className="font-inter text-reach-black/70 text-base" data-id="element-690">
                Base rate $125 per athlete per session
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto" data-id="element-691">
              {PRIVATE_2ON1.map(p => <PlanCard key={`${p.name}-${p.freq}`} plan={p} data-id="element-692" />)}
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-reach-gray py-16 md:py-20" data-id="element-693">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-694">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-695">
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-696">
              <h3 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-697">
                Who 1-on-1 is for
              </h3>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-698">
                Coming off injury. In-season position work. Recruiting-stage
                athletes who need every rep to count. Collegiate and pro prep.
                Anyone who wants the fastest possible progress with full coach
                attention.
              </p>
            </div>
            <div className="bg-white rounded-[4px] p-8 border-l-4 border-reach-red" data-id="element-699">
              <h3 className="font-bebas text-2xl md:text-3xl tracking-wide mb-4" data-id="element-700">
                Who 2-on-1 is for
              </h3>
              <p className="font-inter text-reach-black text-base leading-relaxed" data-id="element-701">
                Siblings. Training partners. Teammates. Private-level coaching
                at a lower per-athlete rate. Both athletes commit to the same
                schedule and frequency.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Every Private Membership Includes */}
      <section className="bg-white py-16 md:py-20" data-id="element-702">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-703">
          <div className="text-center mb-10" data-id="element-704">
            <p className="font-inter text-reach-red text-xs uppercase tracking-[0.3em] font-semibold mb-3" data-id="element-705">
              Every Private Membership Includes
            </p>
            <h2 className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-706">
              What's Included
            </h2>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-reach-gray rounded-[4px] p-6 md:p-8 border-l-4 border-reach-red" data-id="element-707">
            {PRIVATE_INCLUDED.map(item => <li key={item} className="flex items-start gap-3" data-id="element-708">
                <div className="w-6 h-6 bg-reach-red rounded-sm flex items-center justify-center shrink-0 mt-0.5" data-id="element-709">
                  <Check size={14} className="text-white" strokeWidth={3} data-id="element-710" />
                </div>
                <span className="font-inter text-reach-black text-base" data-id="element-711">
                  {item}
                </span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Fine Print */}
      <section className="bg-reach-gray py-12" data-id="element-712">
        <div className="max-w-4xl mx-auto px-4 md:px-8" data-id="element-713">
          <p className="font-inter text-reach-black/70 text-sm leading-relaxed text-center" data-id="element-714">
            Monthly memberships auto renew. Cancel anytime with 7 days notice
            before the next billing cycle. Missed sessions can be made up within
            the same week with 24 hour notice. 2-on-1 pricing is per athlete.
            Both athletes are billed separately. Sibling discount stacks on
            2-on-1 packages when both athletes are siblings. Private sessions
            run 60 or 75 minutes depending on age and goals.
          </p>
        </div>
      </section>
    </>;
}
/* ---------- Subcomponents ---------- */
function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button role="tab" aria-selected={active} onClick={onClick} className={`flex-1 sm:flex-none px-4 sm:px-6 py-2.5 min-h-[44px] rounded-[4px] font-inter text-xs sm:text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${active ? 'bg-reach-black text-white' : 'text-reach-black/70 hover:text-reach-black'}`} data-id="element-715">
      {children}
    </button>;
}
function PlanCard({
  plan
}: {
  plan: Plan;
}) {
  const isDark = !!plan.hero;
  return <div className={`relative rounded-[4px] p-6 md:p-7 flex flex-col transition-all duration-200 ease-out overflow-hidden border-b-4 ${isDark ? 'bg-reach-black text-white ring-2 ring-reach-red border-transparent' : 'bg-reach-gray text-reach-black border-transparent hover:-translate-y-1 hover:border-reach-red'}`} data-id="element-716">
      <div className="mt-2" data-id="element-717">
        <p className="font-inter text-xs uppercase tracking-[0.25em] font-semibold mb-2 text-reach-red" data-id="element-718">
          {plan.freq}
        </p>
        <h3 className="font-bebas text-2xl md:text-3xl tracking-wide leading-none mb-2" data-id="element-719">
          {plan.name}
        </h3>
        {plan.ageNote && <p className={`font-inter text-xs uppercase tracking-wider mb-5 ${isDark ? 'text-white/60' : 'text-reach-black/60'}`} data-id="element-720">
            {plan.ageNote}
          </p>}
        {plan.body && <p className={`font-inter text-sm mb-5 leading-relaxed ${isDark ? 'text-white/85' : 'text-reach-black/75'}`} data-id="element-721">
            {plan.body}
          </p>}
      </div>

      <div className="mb-6 flex items-baseline gap-2 flex-wrap" data-id="element-722">
        <span className="font-bebas text-4xl md:text-5xl tracking-wide leading-none" data-id="element-723">
          {plan.price}
        </span>
        <span className={`font-inter text-sm ${isDark ? 'text-white/70' : 'text-reach-black/60'}`} data-id="element-724">
          {plan.priceNote}
        </span>
        {plan.savings && <span className={`ml-auto inline-block font-inter text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-[4px] ${isDark ? 'bg-white text-reach-red' : 'bg-reach-red text-white'}`} data-id="element-725">
            {plan.savings}
          </span>}
      </div>

      <ul className="space-y-2.5 mb-7 flex-grow" data-id="element-726">
        {plan.features.map(f => <li key={f} className={`flex items-start gap-2.5 text-sm ${isDark ? 'text-white/85' : 'text-reach-black/85'}`} data-id="element-727">
            <Check size={16} className="text-reach-red shrink-0 mt-0.5" data-id="element-728" />
            <span data-id="element-729">{f}</span>
          </li>)}
      </ul>

      <Link to="/book" className={isDark ? 'btn-primary w-full' : 'btn-secondary w-full'} data-id="element-730">
        Enroll Now <ArrowRight size={16} className="ml-2" data-id="element-731" />
      </Link>
    </div>;
}